<template>
    <section class="class-edit-view">
        <div class="modal-header">
            <h3 v-if="course.uuid">Class Edit</h3>
            <h3 v-else>Add Class</h3>
            <p class="required-fields">All fields marked with * are required</p>
        </div>
        <app-form
            ref="classForm"
            :model="course"
            :rules="rules"
            @submit.native.prevent="submit">
            <grid>
                <grid-item width="12">
                    <app-form-item label="Title" prop="title">
                        <app-input id="title" v-model="course.title" autofocus/>
                    </app-form-item>
                </grid-item>

                <grid-item width="6">
                    <app-form-item label="CRN (Cource Ref. Number)" prop="crn">
                        <app-input id="crn" v-model="course.crn"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="6">
                    <app-form-item label="Course Number" prop="course_number">
                        <app-input id="course_number" v-model="course.course_number"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="8">
                    <app-form-item label="Location" prop="location">
                        <app-select
                            id="location"
                            v-model="course.location_id"
                            filterable
                            clearable
                            placeholder="Select">
                            <app-option
                                v-for="item in locations"
                                :key="item.id"
                                :label="item.name"
                                :value="item.id"/>
                        </app-select>
                    </app-form-item>
                </grid-item>

                <grid-item width="6">
                    <app-form-item label="Class Start Time (i.e. 11:00am)" prop="start_time">
                        <app-input id="start_time" v-model="course.start_time"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="6">
                    <app-form-item label="Class End Time (i.e. 1:00pm)" prop="end_time">
                        <app-input id="end_time" v-model="course.end_time"/>
                    </app-form-item>
                </grid-item>

                <grid-item width="6">
                    <app-form-item label="Semester Start" prop="semester_start">
                        <app-date-picker
                            id="semester_start"
                            v-model="course.semester_start"
                            format="MM/dd/yyyy"
                            title="The user can either key in date using format mm/dd/yyyy or use the calendar modal to select a date"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="6">
                    <app-form-item label="Semester End" prop="semester_end">
                        <app-date-picker
                            id="semester_end"
                            v-model="course.semester_end"
                            format="MM/dd/yyyy"
                            title="The user can either key in date using format mm/dd/yyyy or use the calendar modal to select a date"/>
                    </app-form-item>
                </grid-item>
            </grid>

            <app-form-item label="Days Class Meets">
                <app-checkbox v-model="course.meet_sun" label="Sun" border/>
                <app-checkbox v-model="course.meet_mon" label="Mon" border/>
                <app-checkbox v-model="course.meet_tue" label="Tue" border/>
                <app-checkbox v-model="course.meet_wed" label="Wed" border/>
                <app-checkbox v-model="course.meet_thu" label="Thu" border/>
                <app-checkbox v-model="course.meet_fri" label="Fri" border/>
                <app-checkbox v-model="course.meet_sat" label="Sat" border/>
            </app-form-item>

            <app-collapse-item title="Advanced Settings" name="1">
                <app-alert>Use these settings to fine tune how attendance is taken and the course record behaves. <strong>These settings could break attendance.</strong></app-alert>
                <grid>
                    <grid-item width="12"><br></grid-item>
                    <grid-item width="4">
                        <app-form-item prop="late_time">
                            <div slot="label">
                                Late Time
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        The number of minutes<br >
                                        before a student is marked<br >
                                        as being late for the class.
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>

                            <app-input id="late_time" v-model.number="course.late_time">
                                <template slot="append">minutes</template>
                            </app-input>
                        </app-form-item>
                    </grid-item>
                    <grid-item width="4">
                        <app-form-item prop="absent_time">
                            <div slot="label">
                                Absent Time
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        The number of minutes<br >
                                        before a student is marked<br >
                                        absent for the class.
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>
                            <app-input id="absent_time" v-model.number="course.absent_time">
                                <template slot="append">minutes</template>
                            </app-input>
                        </app-form-item>
                    </grid-item>
                    <grid-item width="4">
                        <app-form-item label="Min Percentage" prop="min_percentage">
                            <div slot="label">
                                Min Percentage
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        The minimum percentage<br >
                                        of the class a student has<br >
                                        to be present for credit.
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>
                            <app-input id="min_percentage" v-model.number="course.min_percentage">
                                <template slot="append">%</template>
                            </app-input>
                        </app-form-item>
                    </grid-item>
                    <grid-item width="12">
                        <app-alert
                            v-if="isEmpty(course.leave_time) || isEmpty(course.absent_time) || isEmpty(course.late_time)"
                            width="12"
                            style="margin-bottom: 15px">
                            If any values are left empty they will be replaced with school defaults.
                        </app-alert>
                    </grid-item>
                    <grid-item width="12"><br></grid-item>
                    <grid-item width="4">
                        <app-form-item prop="manual_attendance">
                            <div slot="label">
                                Manual Attendance
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        If attendance is going to be<br >
                                        taken manually even if there<br >
                                        is no beacon present (QR Code).
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>
                            <app-switch 
                                id="manual_attendance" 
                                v-model="course.manual_attendance" 
                                active-text="Yes"
                                inactive-text="No"/>
                        </app-form-item>
                    </grid-item>
                    <grid-item width="4">
                        <app-form-item prop="locked">
                            <div slot="label">
                                Locked
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        Prevent automated imports from<br >
                                        dropping students from this class.<br >
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>
                            <app-switch 
                                id="locked" 
                                v-model="course.locked" 
                                active-text="Yes"
                                inactive-text="No"/>
                        </app-form-item>
                    </grid-item>
                    <grid-item width="4">
                        <app-form-item prop="alerts_students">
                            <div slot="label">
                                Alerts Students
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        Should students receive push<br >
                                        notifications at the start of<br >
                                        class if they are not present.
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>
                            <app-switch 
                                id="alerts_students" 
                                v-model="course.alerts_students" 
                                active-text="Yes"
                                inactive-text="No"/>
                        </app-form-item>
                    </grid-item>
                </grid>
            </app-collapse-item>

            <div class="modal-footer">
                <app-button
                    :loading="isSaving"
                    type="primary"
                    native-type="submit"
                    size="large">Save</app-button>

                <app-button
                    v-if="course.uuid && role <= 7"
                    :border="false"
                    type="danger"
                    size="large"
                    tabindex="1"
                    style="float:left"
                    plain
                    @click="remove">Delete</app-button>
            </div>

        </app-form>
    </section>
</template>
<script>
import { mapGetters } from 'vuex'
import { isEmpty } from '@/utilities'
import moment from 'moment'
import Course from '@/store/models/Course'
import Location from '@/store/models/Location'

export default {
    name: 'page-courses-detail-edit',
    components: {},
    inject: ['modalOptions'],
    props: ['course_id'],
    data() {
        return {
            isSaving: false,
            advancedSettings: [],
            course: Course.$cloneOrNew(this.course_id),
            rules: {
                title: [
                    {
                        required: true,
                        message: 'Please enter a title',
                        trigger: 'blur'
                    }
                ],
                crn: [
                    {
                        required: true,
                        message: 'Please enter a course reference number',
                        trigger: 'blur'
                    }
                ],
                start_time: [
                    {
                        required: true,
                        message: 'Please enter a start time',
                        trigger: 'blur'
                    },
                    { validator: this.checkTimeFormat, trigger: 'blur' }
                ],
                end_time: [
                    {
                        required: true,
                        message: 'Please enter an end time',
                        trigger: 'blur'
                    },
                    { validator: this.checkTimeFormat, trigger: 'blur' }
                ],
                semester_start: [
                    {
                        required: true,
                        type: 'date',
                        message: 'Please enter the semester start date',
                        trigger: 'blur'
                    }
                ],
                semester_end: [
                    {
                        required: true,
                        type: 'date',
                        message: 'Please enter the semester end date',
                        trigger: 'blur'
                    }
                ]
            }
        }
    },
    computed: {
        locations() {
            return Location.query()
                .orderBy('name')
                .get()
        },
        ...mapGetters(['role'])
    },
    mounted() {
        if (this.course.start_time) {
            this.course.start_time = moment(this.course.start_time,
                'HH:mm:ss').format('h:mma')
        }
        if (this.course.end_time) {
            this.course.end_time = moment(this.course.end_time,
                'HH:mm:ss').format('h:mma')
        }
        if (this.course.semester_start) {
            this.course.semester_start = moment(this.course.semester_start).toDate()
        }
        if (this.course.semester_end) {
            this.course.semester_end = moment(this.course.semester_end).toDate()
        }

        this.getLocations({ page: 0, per_page: 9999 })
    },
    methods: {
        getLocations(params) {
            Location.$fetchAll(params)
        },
        checkTimeFormat(rule, value, callback) {
            let format = /^((1?[0-2]|2[0-3])|([0]?[1-9]|2[0-3])):[0-5][0-9](?::[0-5][0-9])? ?(?:am|pm|AM|PM)/
            if (!format.test(value)) {
                callback(new Error('Format must be: hh:mm am/pm (i.e.12:00pm)'))
            }
            else {
                callback()
            }
        },
        submit() {
            /// Check if the form validates
            this.$refs['classForm'].validate(valid => {
                if (valid) {
                    this.course.uuid ? this.update() : this.create()
                }
            })
        },
        create() {
            this.isSaving = true
            this.course.$create()
                .then(({ data }) => {
                    this.isSaving = false
                    this.modalOptions.modalOpen = false
                    this.$router.push({
                        name: 'courses.detail',
                        params: { uuid: data.uuid }
                    })
                })
                .catch(() => {
                    this.isSaving = false
                })
            return false
        },
        update() {
            this.isSaving = true
            this.course.$update()
                .then(() => {
                    this.isSaving = false
                    this.modalOptions.modalOpen = false
                })
            return false
        },
        remove() {
            this.$confirm({
                title: 'Confirm',
                message:
                    'Are you sure you want to remove this class? You cannot undo this action.'
            })
                .then(() => {
                    return this.course.$remove()
                })
                .then(() => {
                    this.$router.push({ name: 'courses' })
                })
             
        },
        isEmpty
    }
}
</script>

<style lang="stylus">
.class-edit-view
    .el-checkbox.is-bordered
        padding-right: 14px
        padding-left: 12px
        line-height: initial

        .el-checkbox__input
            padding-left: 0px

        span
            padding-left: 7px

    .el-checkbox+.el-checkbox, .el-checkbox.is-bordered+.el-checkbox.is-bordered
        margin-left: 2px

    .app-form-item
        .app-form-item__label
            position: relative
            z-index: 2
</style>
